import {type Type, Types, getSystem, withSystem} from '@packages/systems/wfdl';
import {findLastIndex} from 'lodash';
import {TRecord} from '@packages/systems/wfdl-ast';
import {increment, slugify} from '@packages/utilities/string-utils';

type NewDataTypeFromGroupMovementArgs = {
  sectionLabel: string;
  oldFields: {
    [key: string]: Type;
  };
  nextFields: {
    [key: string]: Type;
  };
  fieldId: string;
};

type NewDataTypeFromGroupRemovalArgs = {
  oldFields: {
    [key: string]: Type;
  };
  nextFields: {
    [key: string]: Type;
  };
  fieldId: string;
};

type NewDataTypeFromNewGroupedPropertyArgs = NewDataTypeFromGroupMovementArgs;
type NewDataTypeFromNewUngroupedPropertyArgs = NewDataTypeFromGroupRemovalArgs;

export const newDataTypeFromGroupMovement = ({
  sectionLabel,
  oldFields,
  fieldId,
  nextFields,
}: NewDataTypeFromGroupMovementArgs) => {
  const fieldIds = Object.keys(oldFields);

  // Get all property names in the target group for collision checking
  const targetGroupPropertyNames = fieldIds
    .filter((id) => id !== fieldId)
    .map((id) => {
      const field = oldFields[id];
      if (!field) return null;
      const label = Types.getLabel(field);
      const {propertyName, groupName} = parsePropertyLabel(label);
      return groupName === sectionLabel ? propertyName : null;
    })
    .filter((s): s is NonNullable<string> => Boolean(s));

  // Get the moving field's label and check for collision
  const movingField = oldFields[fieldId];
  if (!movingField) {
    throw new Error(
      `Error removing prop from group - Field with id "${fieldId}" not found in oldFields`
    );
  }
  const movingFieldLabel = Types.getLabel(movingField);
  const {propertyName} = parsePropertyLabel(movingFieldLabel);

  // If there's a collision, create a new incremented name
  if (
    targetGroupPropertyNames
      .map((s) => slugify(s))
      .includes(slugify(propertyName))
  ) {
    const incrementedName = increment(targetGroupPropertyNames, propertyName);
    nextFields[fieldId] = withSystem(
      {
        ...getSystem(movingField),
        label: createPropertyLabel({
          groupName: sectionLabel,
          propertyName: incrementedName,
        }),
      },
      movingField
    );
  }

  const currentIndex = fieldIds.findIndex(
    (currentFieldId) => currentFieldId === fieldId
  );

  const lastIndexOfSection = findLastIndex(fieldIds, (currentFieldId: string) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    Types.getLabel(oldFields[currentFieldId]).startsWith(sectionLabel)
  );

  let newIndex = fieldIds.length;

  if (lastIndexOfSection !== -1) {
    newIndex =
      currentIndex > lastIndexOfSection
        ? lastIndexOfSection + 1
        : lastIndexOfSection;
  }

  const newFieldIds = [...fieldIds];
  newFieldIds.splice(currentIndex, 1);
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  newFieldIds.splice(newIndex, 0, fieldIds[currentIndex]);

  return TRecord(
    newFieldIds.reduce(
      (obj: Record<string, Type>, key: string) =>
        Object.assign(obj, {
          [key]: nextFields[key],
        }),
      {}
    )
  );
};

export const newDataTypeFromGroupRemoval = ({
  oldFields,
  fieldId,
  nextFields,
}: NewDataTypeFromGroupRemovalArgs) => {
  const fieldIds = Object.keys(oldFields);

  // Get all ungrouped property names for collision checking
  const ungroupedPropertyNames = fieldIds
    .filter((id) => id !== fieldId)
    .map((id) => {
      const field = oldFields[id];
      if (!field) return null;
      const label = Types.getLabel(field);
      const {propertyName, groupName} = parsePropertyLabel(label);
      return !groupName ? propertyName : null;
    })
    .filter(Boolean) as string[];

  // Get the moving field's label and check for collision
  const movingField = oldFields[fieldId];
  if (!movingField) {
    throw new Error(
      `Error removing prop from group - Field with id "${fieldId}" not found in oldFields`
    );
  }
  const movingFieldLabel = Types.getLabel(movingField);
  const {propertyName} = parsePropertyLabel(movingFieldLabel);

  // If there's a collision, create a new incremented name
  if (ungroupedPropertyNames.includes(propertyName)) {
    const incrementedName = increment(ungroupedPropertyNames, propertyName);
    nextFields[fieldId] = withSystem(
      {...getSystem(movingField), label: incrementedName},
      movingField
    );
  }

  const currentIndex = fieldIds.findIndex(
    (currentFieldId) => currentFieldId === fieldId
  );

  const firstIndexOfSection = fieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToAnySection(oldFields[currentFieldId])
  );

  let newIndex = fieldIds.length;

  if (firstIndexOfSection !== -1) {
    newIndex = firstIndexOfSection;
  }

  const newFieldIds = [...fieldIds];
  newFieldIds.splice(currentIndex, 1);
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  newFieldIds.splice(newIndex, 0, fieldIds[currentIndex]);

  return TRecord(
    newFieldIds.reduce(
      (obj: Record<string, Type>, key: string) =>
        Object.assign(obj, {
          [key]: nextFields[key],
        }),
      {}
    )
  );
};

export const newDataTypeFromNewGroupedProperty = ({
  sectionLabel,
  oldFields,
  fieldId,
  nextFields,
}: NewDataTypeFromNewGroupedPropertyArgs) => {
  const fieldIds = Object.keys(oldFields);

  const lastIndexOfSection = findLastIndex(fieldIds, (currentFieldId: string) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToSpecificSection(oldFields[currentFieldId], sectionLabel)
  );

  if (lastIndexOfSection === -1) {
    return TRecord(nextFields);
  }

  const newFieldIds = [...fieldIds];
  newFieldIds.splice(lastIndexOfSection + 1, 0, fieldId);

  return TRecord(
    newFieldIds.reduce(
      (obj: Record<string, Type>, key: string) =>
        Object.assign(obj, {
          [key]: nextFields[key],
        }),
      {}
    )
  );
};

export const newDataTypeFromNewUngroupedProperty = ({
  oldFields,
  fieldId,
  nextFields,
}: NewDataTypeFromNewUngroupedPropertyArgs) => {
  const fieldIds = Object.keys(oldFields);

  const firstIndexOfSection = fieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToAnySection(oldFields[currentFieldId])
  );
  let newIndex = fieldIds.length;
  if (firstIndexOfSection !== -1) {
    newIndex = firstIndexOfSection;
  }

  const newFieldIds = [...fieldIds];
  newFieldIds.splice(newIndex, 0, fieldId);

  return TRecord(
    newFieldIds.reduce(
      (obj: Record<string, Type>, key: string) =>
        Object.assign(obj, {
          [key]: nextFields[key],
        }),
      {}
    )
  );
};

export const newDataTypeFromGroupRenaming = ({
  fields,
  oldGroupName,
  newGroupName,
}: {
  fields: {
    [key: string]: Type;
  };
  oldGroupName: string;
  newGroupName: string;
}) => {
  if (normalizeSectionLabel(newGroupName) === '') {
    return TRecord(fields);
  }

  const groupNameAlreadyExists = Object.keys(fields).some((fieldId: string) =>
    fieldBelongsToSpecificSection(
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      fields[fieldId],
      normalizeSectionLabel(newGroupName)
    )
  );

  if (
    groupNameAlreadyExists &&
    normalizeSectionLabel(oldGroupName) !== normalizeSectionLabel(newGroupName)
  ) {
    return TRecord(fields);
  }

  return TRecord(
    Object.keys(fields).reduce((obj: Record<string, Type>, fieldId: string) => {
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      const currentLabel = Types.getLabel(fields[fieldId]);
      const {groupName, propertyName} = parsePropertyLabel(currentLabel);

      // @ts-expect-error - TS2322 - Type '{} | { type: "Element"; meta?: TextMeta | undefined; system?: DataTypeSystem<any[]> | undefined; } | { type: "Generic"; val: { arg: string; body: Type; }; system?: DataTypeSystem<...> | undefined; meta?: MetaAttributes | undefined; } | ... 6 more ... | { ...; }' is not assignable to type 'Type'.
      let newField: Type = {...fields[fieldId]};

      if (
        groupName &&
        normalizeSectionLabel(groupName) === normalizeSectionLabel(oldGroupName)
      ) {
        const system = getSystem(newField);
        newField = withSystem(
          {
            ...system,
            label: createPropertyLabel({
              groupName: newGroupName,
              propertyName,
            }),
          },
          newField
        );
      }

      return Object.assign(obj, {
        [fieldId]: newField,
      });
    }, {})
  );
};

export const newDataTypeFromGroupDeletion = ({
  fields,
  groupName: groupNameToBeDeleted,
}: {
  fields: {
    [key: string]: Type;
  };
  groupName: string;
}) => {
  const fieldIds = Object.keys(fields);

  // Get all ungrouped property names for collision checking
  const ungroupedPropertyNames = fieldIds
    .map((id) => {
      const field = fields[id];
      if (!field) return null;
      const label = Types.getLabel(field);
      const {propertyName, groupName} = parsePropertyLabel(label);
      return !groupName ? propertyName : null;
    })
    .filter(Boolean) as string[];

  const firstIndexOfSection = fieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToAnySection(fields[currentFieldId])
  );

  const firstIndexOfSectionToBeDeleted = fieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToSpecificSection(fields[currentFieldId], groupNameToBeDeleted)
  );

  const lastIndexOfSectionToBeDeleted = findLastIndex(
    fieldIds,
    (currentFieldId) =>
      fieldBelongsToSpecificSection(
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        fields[currentFieldId],
        groupNameToBeDeleted
      )
  );

  if (
    firstIndexOfSection === -1 ||
    firstIndexOfSectionToBeDeleted === -1 ||
    lastIndexOfSectionToBeDeleted === -1
  ) {
    return TRecord(fields);
  }

  const ungroupedIds = fieldIds.slice(0, firstIndexOfSection);
  const groupToBeDeletedIds = fieldIds.slice(
    firstIndexOfSectionToBeDeleted,
    lastIndexOfSectionToBeDeleted + 1
  );

  const remainingIds = [...fieldIds];

  // Remove sections from back to front to maintain correct indices
  remainingIds.splice(
    firstIndexOfSectionToBeDeleted,
    lastIndexOfSectionToBeDeleted - firstIndexOfSectionToBeDeleted + 1
  );
  remainingIds.splice(0, firstIndexOfSection);

  const newFieldsIds = ungroupedIds.concat(groupToBeDeletedIds, remainingIds);

  return TRecord(
    newFieldsIds.reduce((obj: Record<string, Type>, fieldId: string) => {
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      const currentLabel = Types.getLabel(fields[fieldId]);
      const {groupName: currentGroupName, propertyName} =
        parsePropertyLabel(currentLabel);

      // @ts-expect-error - TS2322 - Type '{} | { type: "Element"; meta?: TextMeta | undefined; system?: DataTypeSystem<any[]> | undefined; } | { type: "Generic"; val: { arg: string; body: Type; }; system?: DataTypeSystem<...> | undefined; meta?: MetaAttributes | undefined; } | ... 6 more ... | { ...; }' is not assignable to type 'Type'.
      let newField: Type = {...fields[fieldId]};

      if (
        currentGroupName &&
        normalizeSectionLabel(currentGroupName) ===
          normalizeSectionLabel(groupNameToBeDeleted)
      ) {
        // Check for collision and increment name if needed
        let newPropertyName = propertyName;

        if (ungroupedPropertyNames.includes(propertyName)) {
          newPropertyName = increment(ungroupedPropertyNames, propertyName);
          ungroupedPropertyNames.push(newPropertyName); // Add to list to handle multiple collisions
        }
        const system = getSystem(newField);
        newField = withSystem({...system, label: newPropertyName}, newField);
      }

      return Object.assign(obj, {
        [fieldId]: newField,
      });
    }, {})
  );
};

export const newDataTypeFromGroupReordering = ({
  fields,
  groupDragged,
  groupDroppedTo,
}: {
  fields: {
    [key: string]: Type;
  };
  groupDragged: string;
  groupDroppedTo: string;
}) => {
  const fieldIds = Object.keys(fields);

  const firstIndexOfSectionDragged = fieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToSpecificSection(fields[currentFieldId], groupDragged)
  );
  const lastIndexOfSectionDragged = findLastIndex(fieldIds, (currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToSpecificSection(fields[currentFieldId], groupDragged)
  );
  if (firstIndexOfSectionDragged === -1 || lastIndexOfSectionDragged === -1) {
    return TRecord(fields);
  }

  const firstIndexOfSectionDroppedToBeforeSplice = fieldIds.findIndex(
    (currentFieldId) =>
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      fieldBelongsToSpecificSection(fields[currentFieldId], groupDroppedTo)
  );

  const itemBeingDraggedIsAbove =
    firstIndexOfSectionDroppedToBeforeSplice > firstIndexOfSectionDragged;

  const groupDraggedIds = fieldIds.slice(
    firstIndexOfSectionDragged,
    lastIndexOfSectionDragged + 1
  );

  const newFieldIds = [...fieldIds];
  newFieldIds.splice(
    firstIndexOfSectionDragged,
    lastIndexOfSectionDragged - firstIndexOfSectionDragged + 1
  );

  const firstIndexOfSectionDroppedTo = newFieldIds.findIndex((currentFieldId) =>
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    fieldBelongsToSpecificSection(fields[currentFieldId], groupDroppedTo)
  );
  const lastIndexOfSectionDroppedTo = findLastIndex(
    newFieldIds,
    (currentFieldId) =>
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      fieldBelongsToSpecificSection(fields[currentFieldId], groupDroppedTo)
  );
  if (
    firstIndexOfSectionDroppedTo === -1 ||
    lastIndexOfSectionDroppedTo === -1
  ) {
    return TRecord(fields);
  }

  newFieldIds.splice(
    itemBeingDraggedIsAbove
      ? lastIndexOfSectionDroppedTo + 1
      : firstIndexOfSectionDroppedTo,
    0,
    ...groupDraggedIds
  );

  return TRecord(
    newFieldIds.reduce(
      (obj: Record<string, Type>, fieldId: string) =>
        Object.assign(obj, {
          [fieldId]: fields[fieldId],
        }),
      {}
    )
  );
};

export const parsePropertyLabel = (
  label: string
): {
  groupName: string | null;
  propertyName: string;
} => {
  const delimiterIndex = label.lastIndexOf('/');
  const hasGroupName = delimiterIndex !== -1;

  const groupName: string = hasGroupName
    ? label.substring(0, delimiterIndex)
    : '';

  const propertyName: string = label.substring(delimiterIndex + 1);

  return {
    groupName: groupName.trim() || null,
    propertyName: propertyName.trim(),
  };
};

export const createPropertyLabel = ({
  groupName,
  propertyName,
}: {
  groupName?: string | null;
  propertyName: string;
}): string => {
  const trimmedGroupName =
    typeof groupName === 'string' ? groupName.trim() : '';
  const trimmedPropertyName = propertyName.trim();

  return trimmedGroupName
    ? `${trimmedGroupName}/${trimmedPropertyName}`
    : trimmedPropertyName;
};

export const normalizeSectionLabel = (sectionLabel: string) => {
  return sectionLabel.trim().toLowerCase();
};

export const getDataTypeWithoutGroupNames = (dataType: Type) => {
  const fields = Types.getRecordFields(dataType);
  if (fields) {
    return TRecord(
      Object.keys(fields).reduce((obj: Record<string, Type>, key: string) => {
        const currentField = fields[key];

        if (!currentField) return obj;

        const currentLabel = Types.getLabel(currentField);

        if (!currentLabel && currentField) {
          obj[key] = currentField;
          return obj;
        }

        const newField = withSystem(
          {
            ...getSystem(currentField),
            label: parsePropertyLabel(currentLabel).propertyName,
          },
          currentField
        );

        obj[key] = newField;
        return obj;
      }, {})
    );
  } else {
    return dataType;
  }
};

const propertyLabelHasSection = (label: string): boolean => {
  return label.includes('/');
};

const fieldBelongsToAnySection = (field: Type) => {
  const fullLabel = Types.getLabel(field);
  return propertyLabelHasSection(fullLabel);
};

const fieldBelongsToSpecificSection = (field: Type, sectionLabel: string) => {
  const fullLabel = Types.getLabel(field);
  return fieldLabelBelongsToSpecificSection(fullLabel, sectionLabel);
};

const fieldLabelBelongsToSpecificSection = (
  fieldLabel: string,
  sectionLabel: string
) => {
  const currentFieldSectionLabel = parsePropertyLabel(fieldLabel).groupName;
  return Boolean(
    currentFieldSectionLabel &&
    normalizeSectionLabel(currentFieldSectionLabel) ===
      normalizeSectionLabel(sectionLabel)
  );
};

export const filterLabelsBySection = (
  fieldLabels: string[],
  sectionLabel: string | null
) => {
  if (!sectionLabel) {
    return fieldLabels.filter((field) => !propertyLabelHasSection(field));
  }
  return fieldLabels.filter((field) =>
    fieldLabelBelongsToSpecificSection(field, sectionLabel)
  );
};
